import {
    BlockStack,
    Box,
    Button,
    Card,
    IndexTable,
    InlineStack,
    Layout,
    Page,
    SkeletonBodyText,
    SkeletonPage,
    Text,
} from '@shopify/polaris';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';

// List page for guides owned by the current shop.
const ProductGuideListPage: React.FC = () => {
    const navigate = useNavigate();
    const { productGuideStore } = useStore();
    const { productGuides, loadProductGuides, loadingInitial } = productGuideStore;

    // Access is enforced by the API; the client route stays usable for direct Shopify Admin links.
    useEffect(() => {
        loadProductGuides();
    }, [loadProductGuides]);

    if (loadingInitial) {
        return (
            <SkeletonPage title="Product Guides">
                <Layout>
                    <Layout.Section>
                        <Box padding="400">
                            <SkeletonBodyText />
                        </Box>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    const rows = productGuides.map((guide, index) => (
        <IndexTable.Row id={String(guide.id)} key={guide.id} position={index}>
            <IndexTable.Cell>
                <BlockStack gap="100">
                    <Text as="span" variant="bodyMd" fontWeight="bold">
                        {guide.name}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                        {guide.handle}
                    </Text>
                </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>{guide.isActive ? 'Active' : 'Draft'}</IndexTable.Cell>
            <IndexTable.Cell>{guide.questionCount}</IndexTable.Cell>
            <IndexTable.Cell>{guide.resultGroupCount}</IndexTable.Cell>
            <IndexTable.Cell>
                {guide.updatedAt || guide.createdAt
                    ? new Date(guide.updatedAt || guide.createdAt).toLocaleDateString()
                    : '-'}
            </IndexTable.Cell>
            <IndexTable.Cell>
                <InlineStack align="end">
                    <Button onClick={() => navigate(`/product-guides/${guide.id}`)}>Edit</Button>
                </InlineStack>
            </IndexTable.Cell>
        </IndexTable.Row>
    ));

    return (
        <Page
            title="Product Guides"
            primaryAction={{
                content: 'Create guide',
                onAction: () => navigate('/product-guides/new'),
            }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        {productGuides.length === 0 ? (
                            <Box padding="500">
                                <BlockStack gap="400" align="center">
                                    <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                                        No product guides have been created for this store.
                                    </Text>
                                    <Button variant="primary" onClick={() => navigate('/product-guides/new')}>
                                        Create guide
                                    </Button>
                                </BlockStack>
                            </Box>
                        ) : (
                            <IndexTable
                                resourceName={{ singular: 'product guide', plural: 'product guides' }}
                                itemCount={productGuides.length}
                                selectable={false}
                                headings={[
                                    { title: 'Guide' },
                                    { title: 'Status' },
                                    { title: 'Questions' },
                                    { title: 'Groups' },
                                    { title: 'Updated' },
                                    { title: '' },
                                ]}
                            >
                                {rows}
                            </IndexTable>
                        )}
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default observer(ProductGuideListPage);
